import { Link } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { SocialIcon, SOCIAL } from "../components/SocialIcons";
import GameBanner from "../components/GameBanner";

const SECTIONS = [
  {
    to: "/study",
    icon: "📚",
    label: "Study Materials",
    desc: "Nursing notes, organized by subject. Concise. Caffeinated. Clinically relevant.",
  },
  {
    to: "/paintings",
    icon: "🎨",
    label: "Paintings & Me",
    desc: "Acrylic and a suspicious amount of mountain imagery.",
  },
  {
    to: "/thecat",
    icon: "🐱",
    label: "My Tuki",
    desc: "A gallery dedicated entirely to one of the most important being in my life.",
  },
  {
    to: "/aboutme",
    icon: "🧕🏼",
    label: "About Me",
    desc: "The human behind the notes, the paint, and the cat obsession.",
  },
];

const WEBSITE_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": "https://5minutenursingschool.com/#website",
      "url": "https://5minutenursingschool.com",
      "name": "5 Minute Nursing School",
      "description": "Free, concise nursing notes, study guides, and mnemonics by Farjana Yasmin Mim.",
      "publisher": { "@id": "https://5minutenursingschool.com/#organization" },
    },
    {
      "@type": "Organization",
      "@id": "https://5minutenursingschool.com/#organization",
      "name": "5 Minute Nursing School",
      "url": "https://5minutenursingschool.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://5minutenursingschool.com/logo512.png",
        "width": 512,
        "height": 512,
      },
      "image": {
        "@type": "ImageObject",
        "url": "https://5minutenursingschool.com/OG-Image.png",
        "width": 1200,
        "height": 630,
      },
      "sameAs": [
        "https://www.youtube.com/@Caffei_N",
        "https://www.instagram.com/caffei_n_",
        "https://www.facebook.com/caffeiN0w0",
        "https://www.pinterest.com/caffei_n",
      ],
    },
  ],
};

export default function Home() {
  const { dark } = useTheme();

  return (
    <div className={`min-h-screen transition-colors duration-300 ${dark ? "bg-gray-900" : "bg-gradient-to-br from-stone-50 to-emerald-50"}`}>
      <title>5 Minute Nursing School — Free Nursing Notes &amp; Study Guides</title>
      <meta name="description" content="Free, concise nursing notes, study guides, and mnemonics by Farjana Yasmin Mim. Clear summaries for nursing students who need answers fast." />
      <link rel="canonical" href="https://5minutenursingschool.com/" />
      <script type="application/ld+json">{JSON.stringify(WEBSITE_LD)}</script>
      {/* Hero */}
      <section className="flex flex-col items-center justify-center min-h-[60vh] px-6 pt-20 pb-10 text-center">
        <div
          className={`inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-6
            ${dark ? "bg-emerald-900/40 text-emerald-400" : "bg-emerald-100 text-emerald-700"}`}
        >
          5 Minute Nursing School
        </div>

        <h1 className={`text-5xl md:text-6xl font-bold mb-4 leading-tight ${dark ? "text-white" : "text-gray-900"}`}>
          Hello, I'm{" "}
          <span className="text-emerald-600 dark:text-emerald-400">Farjana</span> 🩺
        </h1>

        <p className={`text-lg md:text-xl max-w-2xl leading-relaxed mb-2 ${dark ? "text-gray-300" : "text-gray-600"}`}>
          Teacher, Nursing student by vocation, note-maker by compulsion, cat mom by unconditional love.
          Sharing everything so you don't have to suffer alone.
        </p>
        <p className={`text-sm ${dark ? "text-gray-500" : "text-gray-400"}`}>📍 Dhaka, Bangladesh</p>

        {/* Quick nav pills */}
        <div className="flex flex-wrap gap-3 mt-8 justify-center">
          {SECTIONS.map(({ to, label, icon }) => (
            <Link
              key={to}
              to={to}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium shadow-sm transition-all duration-200
                hover:shadow-md hover:-translate-y-0.5
                ${dark
                  ? "bg-gray-800 text-gray-200 hover:bg-gray-700 border border-gray-700"
                  : "bg-white text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 border border-gray-100"
                }`}
            >
              {icon} {label}
            </Link>
          ))}
        </div>
      </section>

      {/* Section cards */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {SECTIONS.map(({ to, icon, label, desc }) => (
            <Link
              key={to}
              to={to}
              className={`group p-6 rounded-2xl border transition-all duration-200 hover:-translate-y-1 hover:shadow-xl
                ${dark
                  ? "bg-gray-800 border-gray-700 hover:border-emerald-600"
                  : "bg-white border-gray-100 shadow-sm hover:border-emerald-300"
                }`}
            >
              <div className="text-4xl mb-3">{icon}</div>
              <h2
                className={`text-xl font-semibold mb-2 transition-colors
                  group-hover:text-emerald-600 dark:group-hover:text-emerald-400
                  ${dark ? "text-white" : "text-gray-900"}`}
              >
                {label}
              </h2>
              <p className={`text-sm leading-relaxed ${dark ? "text-gray-400" : "text-gray-500"}`}>
                {desc}
              </p>
            </Link>
          ))}

          {/* Ward Rush — full-width animated retro banner (after About Me) */}
          <GameBanner />
        </div>
      </section>

      {/* Footer */}
      <footer
        className={`border-t py-8 px-6 ${dark ? "border-gray-800 bg-gray-950" : "border-gray-100 bg-white"}`}
      >
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className={`text-sm ${dark ? "text-gray-500" : "text-gray-400"}`}>
            © 2025 5 Minute Nursing School
          </p>
          <div className="flex gap-4 items-center">
            {SOCIAL.map(({ key, label, href }) => (
              <a
                key={key}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                className="block transition-transform hover:scale-110"
              >
                <SocialIcon name={key} size={36} />
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
